import React, { useEffect, useState } from "react";
import {
  Grid,
  Box,
  Typography,
  Button,
  Link,
  Card,
  CardContent,
  CardHeader,
  Stack,
  Chip,
  Divider,
  TextField,
  FormControl,
  Select,
  MenuItem,
} from "@mui/material";
import { LoadingButton } from "@mui/lab";
import { Icon } from "@iconify/react";
import { useTheme } from "@mui/material";
import { Loader } from "src/components/Loader";
import { useDropdown } from "src/hooks/useDropdown.hook";
import { getOcpiRole, getOcpiVersions } from "src/utils/helper";
import { statusColorMap} from "../constants/ocpi.constant";
import { OCPI_CURRENCIES } from "src/utils/ocpi.constants";

// hooks
import { useFetchOnDemand } from "src/hooks/useFetch.hook";


const PartnerDetails = () => {
  const theme = useTheme();
  const { data: ocpiRoles } = useDropdown("OcpiRole");
  const { data: ocpiVersions } = useDropdown("OcpiVersion");
  const { fetchData, isLoading, data } = useFetchOnDemand("getByPartyId", true, false);
  const { mutate, isLoading: isMutating } = useFetchOnDemand("emspMarkupUpsert", !0);
  

  // State for EMSP markup editing
  const [isEditingMarkup, setIsEditingMarkup] = useState(false);
  const [markupForm, setMarkupForm] = useState({
    percentageMarkup: 0,
    fixedMarkup: 0,
    currency: OCPI_CURRENCIES[0].code
  });

  // Update markup form when data changes
  useEffect(() => {
    if (data?.emspMarkup) {
      setMarkupForm({
        percentageMarkup: Number(data.emspMarkup.percentageMarkup) || 0,
        fixedMarkup: Number(data.emspMarkup.fixedMarkup) || 0,
        currency: data.emspMarkup.currency || OCPI_CURRENCIES[0].code
      });
    }
  }, [data]);
  
  useEffect(() => {
    fetchData();
  }, [fetchData]);

  // Handler functions for markup editing
  const handleEditMarkup = () => {
    setIsEditingMarkup(true);
  };

  const handleSaveMarkup = async () => {
    try {
      // Here you would typically make an API call to save the markup configuration
      console.log('Saving markup configuration:', markupForm);
      
      // Simulate API call
      await mutate({
        percentage_markup: markupForm.percentageMarkup,
        fixed_markup: markupForm.fixedMarkup,
        currency: markupForm.currency,
        partner_id: data.partnerId,
      });
      
      // Close edit mode
      if (!isMutating) {
        setIsEditingMarkup(false);
      }
      
      // You could also update the local data state here if needed
    } catch (error) {
      console.error('Failed to save markup configuration:', error);
    }
  };

  const handleCancelEdit = () => {
    setIsEditingMarkup(false);
  };

  const handleMarkupChange = (field, value) => {
    setMarkupForm(prev => ({
      ...prev,
      [field]: value
    }));
  };

  if (isLoading) {
    return <Loader variant="skeleton" skeletonCount={5} skeletonVariant="card" />;
  }

  return (
    <Box sx={{ flexGrow: 1, p: 3 }}>
      {data &&
        <Grid container spacing={3}>
          {/* Partner Details */}
          <Grid item xs={12} md={8} lg={8}>
            <Card variant="outlined">
              <CardHeader
                title={
                  <Stack direction="row" alignItems="center" spacing={1}>
                    <Icon
                      icon="mdi:information-outline"
                      color={theme.palette.primary.main}
                      width="20"
                    />
                    <Typography variant="h6">Partner Details</Typography>
                  </Stack>
                }
              />
              <CardContent>
                <Grid container spacing={2}>
                  <Grid item xs={12} sm={6}>
                    <Typography variant="body2" fontWeight="bold">
                      Business Name
                    </Typography>
                    <Typography variant="body2">{data.partnerName}</Typography>
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <Typography variant="body2" fontWeight="bold">
                      Role
                    </Typography>
                    <Typography variant="body2">{getOcpiRole(ocpiRoles, data.ocpiRole)}</Typography>
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <Typography variant="body2" fontWeight="bold">
                      OCPI Version
                    </Typography>
                    <Typography variant="body2">{getOcpiVersions(ocpiVersions, data.ocpiVersion)}</Typography>
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <Typography variant="body2" fontWeight="bold">
                      Status
                    </Typography>
                    <Chip
                      label={data.status}
                      size="small"
                      color={statusColorMap[data.status].color}
                      sx={{
                        mb: 1, bgcolor: statusColorMap[data.status].bgcolor,
                        color: statusColorMap[data.status].text,
                      }}
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <Typography variant="body2" fontWeight="bold">
                      Website
                    </Typography>
                    <Link
                      href={data.businessWebsite}
                      target="_blank"
                      rel="noopener"
                    >
                      {data.businessWebsite}
                    </Link>
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <Typography variant="body2" fontWeight="bold">
                      Contact Email
                    </Typography>
                    <Typography variant="body2">
                      {data.contactEmail}
                    </Typography>
                  </Grid>
                </Grid>
              </CardContent>
            </Card>
          </Grid>

          {/* Quick Actions */}
          <Grid item xs={12} sm={6} md={4} lg={4}>
            <Card variant="outlined">
              <CardHeader
                title={
                  <Stack direction="row" alignItems="center" spacing={1}>
                    <Icon
                      icon="mdi:flash-outline"
                      color={theme.palette.warning.main}
                      width="20"
                    />
                    <Typography variant="h6">Quick Actions</Typography>
                  </Stack>
                }
              />
              <CardContent>
                <Stack spacing={2}>
                  <Button variant="outlined" fullWidth>
                    Configuration
                  </Button>
                  <Button variant="outlined" fullWidth>
                    Test Connection
                  </Button>
                  <Button variant="outlined" fullWidth>
                    Sync History
                  </Button>
                </Stack>
              </CardContent>
            </Card>
          </Grid>

          {/* Endpoints */}
          <Grid item xs={12} md={6}>
            <Card variant="outlined">
              <CardHeader
                title={
                  <Stack direction="row" alignItems="center" spacing={1}>
                    <Icon
                      icon="mdi:link-variant"
                      color={theme.palette.info.main}
                      width="20"
                    />
                    <Typography variant="h6">Endpoints</Typography>
                  </Stack>
                }
              />
              <CardContent>
                <Stack spacing={2}>
                  {data.endpoints && data.endpoints.map((endpoint, index) => (
                    <Box key={index}>
                      <Chip label={endpoint.identifier} size="small" sx={{ mb: 1 }} />
                      <Typography variant="body2" sx={{ wordBreak: "break-all" }}>
                        {endpoint.url}
                      </Typography>
                    </Box>
                  ))}
                  {!data.endpoints && <Typography variant="body1">
                    No Endpoint Discovered
                  </Typography>}
                </Stack>
              </CardContent>
            </Card>
          </Grid>

          {/* EMSP Markup Configuration and Price Calculation */}
          <Grid item xs={12} md={6}>
            <Stack spacing={3}>
              {/* EMSP Markup Configuration */}
              <Card variant="outlined">
                <CardHeader
                  title={
                    <Stack direction="row" alignItems="center" justifyContent="space-between">
                      <Stack direction="row" alignItems="center" spacing={1}>
                        <Icon
                          icon="mdi:currency-usd"
                          color={theme.palette.success.main}
                          width="20"
                        />
                        <Typography variant="h6">EMSP Markup Configuration</Typography>
                      </Stack>
                      {!isEditingMarkup ? (
                        <Button
                          variant="outlined"
                          size="small"
                          startIcon={<Icon icon="mdi:pencil" width="16" />}
                          onClick={handleEditMarkup}
                        >
                          Edit
                        </Button>
                      ) : (
                        <Stack direction="row" spacing={1}>
                          <LoadingButton
                            variant="contained"
                            size="small"
                            loading={isMutating}
                            startIcon={<Icon icon="mdi:content-save" width="16" />}
                            onClick={handleSaveMarkup}
                          >
                            Save Changes
                          </LoadingButton>
                          <Button
                            variant="outlined"
                            size="small"
                            onClick={handleCancelEdit}
                          >
                            Cancel
                          </Button>
                        </Stack>
                      )}
                    </Stack>
                  }
                />
                <CardContent>
                  <Grid container spacing={2}>
                    <Grid item xs={12} sm={4}>
                      <Typography variant="body2" fontWeight="bold" color="text.secondary" sx={{ mb: 1 }}>
                        Percentage Markup
                      </Typography>
                      {isEditingMarkup ? (
                        <TextField
                          type="number"
                          value={markupForm.percentageMarkup}
                          onChange={(e) => handleMarkupChange('percentageMarkup', parseFloat(e.target.value) || 0)}
                          size="small"
                          fullWidth
                          InputProps={{
                            endAdornment: <Typography variant="body2">%</Typography>
                          }}
                        />
                      ) : (
                        <Typography variant="h6" color="success.main">
                          {markupForm.percentageMarkup}%
                        </Typography>
                      )}
                    </Grid>
                    <Grid item xs={12} sm={4}>
                      <Typography variant="body2" fontWeight="bold" color="text.secondary" sx={{ mb: 1 }}>
                        Fixed Markup
                      </Typography>
                      {isEditingMarkup ? (
                        <TextField
                          type="number"
                          value={markupForm.fixedMarkup}
                          onChange={(e) => handleMarkupChange('fixedMarkup', parseFloat(e.target.value) || 0)}
                          size="small"
                          fullWidth
                          InputProps={{
                            startAdornment: <Typography variant="body2" sx={{ mr: 1 }}>{markupForm.currency}</Typography>
                          }}
                        />
                      ) : (
                        <Typography variant="h6" color="success.main">
                          {markupForm.currency} {markupForm.fixedMarkup}
                        </Typography>
                      )}
                    </Grid>
                    <Grid item xs={12} sm={4}>
                      <Typography variant="body2" fontWeight="bold" color="text.secondary" sx={{ mb: 1 }}>
                        Currency
                      </Typography>
                      {isEditingMarkup ? (
                        <FormControl size="small" fullWidth>
                          <Select
                            value={markupForm.currency}
                            onChange={(e) => handleMarkupChange('currency', e.target.value)}
                            displayEmpty
                          >
                            {OCPI_CURRENCIES.map((currency) => (
                              <MenuItem key={currency.code} value={currency.code}>
                                {currency.name} ({currency.symbol})
                              </MenuItem>
                            ))}
                          </Select>
                        </FormControl>
                      ) : (
                        <Typography variant="h6" color="success.main">
                          {markupForm.currency}
                        </Typography>
                      )}
                    </Grid>
                  </Grid>
                </CardContent>
              </Card>
              
              {/* Price Calculation Example */}
              <Card variant="outlined">
                <CardHeader
                  title={
                    <Stack direction="row" alignItems="center" spacing={1}>
                      <Icon
                        icon="mdi:calculator"
                        color={theme.palette.warning.main}
                        width="20"
                      />
                      <Typography variant="h6">Price Calculation Example</Typography>
                    </Stack>
                  }
                />
                <CardContent>
                  <Stack spacing={3}>
                    {/* Sample Charging Session */}
                    <Box>
                      <Typography variant="subtitle1" fontWeight="bold" color="text.secondary" sx={{ mb: 1 }}>
                        Sample Charging Session
                      </Typography>
                      <Grid container spacing={2}>
                        <Grid item xs={12} sm={4}>
                          <Typography variant="body2" fontWeight="bold" color="text.secondary">
                            Base Price (CPO)
                          </Typography>
                          <Typography variant="body1" fontWeight="bold">
                            {markupForm.currency} 50.00
                          </Typography>
                        </Grid>
                        <Grid item xs={12} sm={4}>
                          <Typography variant="body2" fontWeight="bold" color="text.secondary">
                            Energy Consumed
                          </Typography>
                          <Typography variant="body1" fontWeight="bold">
                            25 kWh
                          </Typography>
                        </Grid>
                        <Grid item xs={12} sm={4}>
                          <Typography variant="body2" fontWeight="bold" color="text.secondary">
                            Base Rate
                          </Typography>
                          <Typography variant="body1" fontWeight="bold">
                            {markupForm.currency} 2/kWh
                          </Typography>
                        </Grid>
                      </Grid>
                    </Box>

                    {/* Markup Calculation */}
                    <Box>
                      <Typography variant="subtitle2" fontWeight="bold" color="text.secondary" sx={{ mb: 2 }}>
                        Markup Calculation
                      </Typography>
                      <Stack spacing={1.5}>
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                          <Typography variant="body2">Base Price</Typography>
                          <Typography variant="body2" fontWeight="bold">{markupForm.currency} 50.00</Typography>
                        </Box>
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                          <Typography variant="body2">Percentage Markup ({markupForm.percentageMarkup}%)</Typography>
                          <Typography variant="body2" fontWeight="bold" color="success.main">+ {markupForm.currency} {(50 * markupForm.percentageMarkup / 100).toFixed(2)}</Typography>
                        </Box>
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                          <Typography variant="body2">Fixed Markup</Typography>
                          <Typography variant="body2" fontWeight="bold" color="success.main">+ {markupForm.currency} {markupForm.fixedMarkup.toFixed(2)}</Typography>
                        </Box>
                        <Divider sx={{ my: 1 }} />
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                          <Typography variant="h6" fontWeight="bold">Final Customer Price</Typography>
                          <Typography variant="h6" fontWeight="bold" color="primary.main">
                            {markupForm.currency} {(50 + (50 * markupForm.percentageMarkup / 100) + markupForm.fixedMarkup).toFixed(2)}
                          </Typography>
                        </Box>
                      </Stack>
                    </Box>
                  </Stack>
                </CardContent>
              </Card>
            </Stack>
          </Grid>
        </Grid>}
    </Box>
  );
};

export default PartnerDetails;
